import { supabase } from './supabase';

export interface Continent {
  id: string;
  name: string;
  code: string;
}

export interface IncidentCount {
  area_id: string;
  area_name: string;
  incident_count: number;
  max_severity?: number;
  threat_level?: number;
  severity_level: 'red' | 'amber' | 'green';
}

// Threat Level Color Scale (1-12)
export const THREAT_COLORS: Record<number, string> = {
  1: '#22c55e',
  2: '#16a34a',
  3: '#84cc16',
  4: '#facc15',
  5: '#eab308',
  6: '#f97316',
  7: '#ea580c',
  8: '#ef4444',
  9: '#dc2626',
  10: '#991b1b',  // Extreme - Very Dark Red
  11: '#7f1d1d',  // Extreme - Near Black Red
  12: '#1a0000'   // Extreme - Black (Critical)
};

export const THREAT_LEVELS = {
  LOW: { min: 1, max: 2, label: 'Low Security Threat', color: '#22c55e' },
  MEDIUM: { min: 3, max: 6, label: 'Medium Security Threat', color: '#eab308' },
  HIGH: { min: 7, max: 9, label: 'High Security Threat', color: '#ef4444' },
  EXTREME: { min: 10, max: 12, label: 'Extreme Security Threat', color: '#1a0000' }
};

export interface IncidentByCategory {
  category: string;
  category_name: string;
  incident_count: number;
}

export interface RecentIncident {
  id: string;
  title: string;
  category: string;
  incident_date: string;
  severity: number;
  location_name: string;
  source_name: string;
}

export interface SecurityIncidentFull {
  id: string;
  continent_id: string;
  country_id: string;
  state_id: string;
  lga_id: string;
  category: string;
  title: string;
  description: string;
  incident_date: string;
  location_name: string;
  location_point: GeoJSON.Point | null;
  severity: number;
  casualties: number;
  injuries: number;
  verified: boolean;
  source_name: string;
  source_url: string;
  created_at: string;
}

export const SEVERITY_THRESHOLDS = {
  country: { red: 10, amber: 5 },
  state: { red: 5, amber: 3 },
  lga: { red: 3, amber: 2 }
};

export const INCIDENT_CATEGORIES = [
  { value: 'armed_robbery', label: 'Armed Robbery' },
  { value: 'kidnapping', label: 'Kidnapping' },
  { value: 'gun_violence', label: 'Gun Violence' },
  { value: 'protest', label: 'Protest' },
  { value: 'mob_action', label: 'Mob Action' },
  { value: 'terrorism', label: 'Terrorism' },
  { value: 'communal_clash', label: 'Communal Clash' },
  { value: 'cultism', label: 'Cultism' },
  { value: 'banditry', label: 'Banditry' },
  { value: 'cyber_crime', label: 'Cyber Crime' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'assault', label: 'Assault' },
  { value: 'murder', label: 'Murder' },
  { value: 'arson', label: 'Arson' },
  { value: 'vandalism', label: 'Vandalism' },
  { value: 'piracy', label: 'Piracy' },
  { value: 'drug_trafficking', label: 'Drug Trafficking' },
  { value: 'human_trafficking', label: 'Human Trafficking' },
  { value: 'other', label: 'Other' }
];

export const incidentService = {
  getContinents: async (): Promise<{ data: Continent[] | null; error: any }> => {
    const result = await supabase
      .from('continents' as any)
      .select('*')
      .order('name');
    return result as any;
  },

  getCountriesByContinent: async (continentId: string) => {
    const result = await supabase
      .from('countries' as any)
      .select('*')
      .eq('continent_id', continentId)
      .order('name');
    return result as any;
  },

  getIncidentCounts: async (
    level: 'country' | 'state' | 'lga',
    parentId?: string,
    months: number = 6
  ): Promise<{ data: IncidentCount[] | null; error: any }> => {
    const result = await (supabase as any).rpc('get_incident_counts', {
      p_level: level,
      p_parent_id: parentId || null,
      p_months: months
    });
    return result as any;
  },

  getIncidentsByCategory: async (
    level: 'country' | 'state' | 'lga',
    areaId?: string,
    months: number = 6
  ): Promise<{ data: IncidentByCategory[] | null; error: any }> => {
    const result = await (supabase as any).rpc('get_incidents_by_category', {
      p_level: level,
      p_area_id: areaId || null,
      p_months: months
    });
    return result as any;
  },

  getRecentIncidents: async (
    level: 'country' | 'state' | 'lga',
    areaId?: string,
    limit: number = 20,
    months: number = 6
  ): Promise<{ data: RecentIncident[] | null; error: any }> => {
    const result = await (supabase as any).rpc('get_recent_incidents', {
      p_level: level,
      p_area_id: areaId || null,
      p_limit: limit,
      p_months: months
    });
    return result as any;
  },

  getIncidentsForArea: async (
    level: 'country' | 'state' | 'lga',
    areaId: string,
    months: number = 6
  ): Promise<{ data: SecurityIncidentFull[] | null; error: any }> => {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);
    const startDateStr = startDate.toISOString().split('T')[0];

    let query = supabase
      .from('security_incidents' as any)
      .select('*')
      .gte('incident_date', startDateStr)
      .order('incident_date', { ascending: false });

    if (level === 'country') {
      query = query.eq('country_id', areaId);
    } else if (level === 'state') {
      query = query.eq('state_id', areaId);
    } else if (level === 'lga') {
      query = query.eq('lga_id', areaId);
    }

    const result = await query;
    return result as any;
  },

  getThreatColor: (level: number): string => {
    const safeLevel = Math.max(1, Math.min(12, Math.round(level)));
    return THREAT_COLORS[safeLevel] || THREAT_COLORS[1];
  },

  getSeverityColor: (count: number, level: 'country' | 'state' | 'lga'): string => {
    const thresholds = SEVERITY_THRESHOLDS[level];
    if (count > thresholds.red) return '#ef4444';
    if (count >= thresholds.amber) return '#f59e0b';
    return '#22c55e';
  },

  getSeverityLevel: (count: number, level: 'country' | 'state' | 'lga'): 'red' | 'amber' | 'green' => {
    const thresholds = SEVERITY_THRESHOLDS[level];
    if (count > thresholds.red) return 'red';
    if (count >= thresholds.amber) return 'amber';
    return 'green';
  },

  buildHeatmap: (incidentCounts: IncidentCount[]): Map<string, number> => {
    const heatmap = new Map<string, number>();
    incidentCounts.forEach(item => {
      heatmap.set(item.area_id, item.incident_count);
    });
    return heatmap;
  },

  getTotalIncidentCount: async (
    level: 'country' | 'state' | 'lga',
    areaId: string,
    months: number = 6
  ): Promise<number> => {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - months);
    const startDateStr = startDate.toISOString().split('T')[0];

    let query = supabase
      .from('security_incidents' as any)
      .select('id', { count: 'exact', head: true })
      .gte('incident_date', startDateStr);

    if (level === 'country') {
      query = query.eq('country_id', areaId);
    } else if (level === 'state') {
      query = query.eq('state_id', areaId);
    } else if (level === 'lga') {
      query = query.eq('lga_id', areaId);
    }

    const { count } = await query;
    return count || 0;
  }
};

export default incidentService;
